import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { downloadFileName } from '../../api/fileResponse';
import {
  downloadOriginatorDocumentFileContent,
  downloadPackageFileContent,
  downloadPackageFileRendition,
  listPackageFileMembers,
  getLoanEvidence,
  getOriginatorDocument,
  getOriginatorDocumentLoanTape,
  getOriginatorDocumentTypes,
  getOriginatorEvidenceDetails,
  getOriginatorEvidencePage,
  getOriginatorExtractedFacts,
  getPortfolioLoan,
  listPackageCopyContexts,
  markOriginatorReviewed,
  patchOriginatorClassification,
  patchOriginatorExtractedFields,
  runOriginatorVaAction,
  verifyOriginatorFact,
  verifyOriginatorFactsBatch,
} from '../../api/originatorApi';
import { saveBlob } from '../../components/custodian/saveBlob';
import { CreateRequestModal } from '../../components/requests/CreateRequestModal';
import { FEATURES } from '../../config/features';
import {
  canMutateDigitizeVa,
  canViewDigitize,
  digitizeDeniedMessage,
} from '../../auth/digitizeAccess';
import { isReadOnlySession } from '../../auth/access';
import { readDemoSession } from '../../auth/demoSession';
import { DigitizeWorkspace } from '../../components/digitize/DigitizeWorkspace';
import { runValidateToCompletion, validateProgressMessage } from '../../digitize/validateSync';
import {
  isSyncJobQueuedResponse,
  syncJobActionLabel,
  syncJobOnLoad,
  syncJobPollOutcome,
} from '../../digitize/syncJob';
import {
  CertifiedLockNotice,
  InCustodyNotice,
  isInCustody,
  isLockedByCertification,
} from '../../components/package/InCustodyNotice';
import { formatCustodyFailure, formatLoadFailure } from '../../api/apiError';
import { isUuid } from '../../utils/format';

function vaActionErrorMessage(action, requestError) {
  if (requestError?.code === 'PACKAGE_IN_CUSTODY') {
    return formatCustodyFailure(requestError);
  }
  const raw = requestError?.message || '';
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
    if (action === 'classify') {
      return (
        'The connection dropped while ABDP was still classifying. 10k–20k PDF / multi-GB batches can take hours. ' +
        'Leave this tab open — Digitize keeps checking. When Process shows Review, use Sync from ABDP.'
      );
    }
    return 'The connection dropped before the server answered. Try again, or Sync from ABDP if the batch is already in Review.';
  }
  if (raw) {
    return action === 'classify' ? `${raw} Still polling ABDP for types — leave this tab open.` : raw;
  }
  return `Unable to run ${action}.`;
}

/**
 * Originator Digitize — classify / extract / review (VA mutate).
 * Providers and custodians use read-only Digitize surfaces elsewhere.
 */
export function OriginatorDigitizePage() {
  const { packageId, assetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const valid = isUuid(assetId);
  const demoSession = readDemoSession();
  const sessionRole = demoSession?.role || 'originator';
  const viewAllowed = canViewDigitize(sessionRole);
  const roleMutateAllowed = canMutateDigitizeVa(sessionRole) && !isReadOnlySession(demoSession);

  const [document, setDocument] = useState(null);
  const [loan, setLoan] = useState(null);
  // Phase 0 custody freeze: once the asset is ACCEPTED into custody every
  // Digitize mutation is refused by the backend (409 PACKAGE_IN_CUSTODY).
  const inCustody = isInCustody(document) || isInCustody(loan);
  // After certification the fact snapshot is hashed into the certificate:
  // values stay read-only until the Provider returns the batch (409 PACKAGE_CERTIFIED).
  const certifiedLock = !inCustody && isLockedByCertification(document);
  const mutateAllowed = roleMutateAllowed && !inCustody && !certifiedLock;
  // Once the batch is certified / with the Custodian nothing may be re-run —
  // not even the ABDP sync actions. Stored values stay visible read-only.
  const canSyncFromAbdp = mutateAllowed;
  const [facts, setFacts] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [packageEvidence, setPackageEvidence] = useState(null);
  const [loanTape, setLoanTape] = useState(undefined);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [loading, setLoading] = useState(valid && viewAllowed);
  const [error, setError] = useState(
    !viewAllowed
      ? digitizeDeniedMessage(sessionRole)
      : valid
        ? ''
        : 'Open a loan from the package Assets view.'
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [vaBusy, setVaBusy] = useState(false);
  const [vaMessage, setVaMessage] = useState('');
  const [pollUntil, setPollUntil] = useState(0);
  // Set when the last VA action was queued as a background job (large batch):
  // the poll then watches ABDP_SYNC_JOB instead of only DOCUMENT_TYPE_* facts.
  const [pollJob, setPollJob] = useState(null);
  // When the current classify was started; older ABDP_SYNC_JOB outcomes are ignored.
  const classifyStartedRef = useRef(0);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [copyContexts, setCopyContexts] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);

  const load = useCallback(async () => {
    if (!valid || !viewAllowed) return;
    setLoading(true);
    setError('');
    try {
      const [
        documentResponse,
        factsResponse,
        loanResponse,
        pkgEvidence,
        typesResponse,
        tapeResponse,
      ] = await Promise.all([
        getOriginatorDocument(assetId).catch(() => null),
        getOriginatorExtractedFacts(assetId).catch(() => null),
        getPortfolioLoan(assetId).catch(() => null),
        packageId ? getLoanEvidence(assetId).catch(() => null) : Promise.resolve(null),
        getOriginatorDocumentTypes(assetId).catch(() => null),
        // 404 = no tape attached (null); other failures stay unknown (undefined).
        Promise.resolve()
          .then(() => getOriginatorDocumentLoanTape(assetId))
          .catch((tapeError) => (tapeError?.status === 404 ? null : undefined)),
      ]);
      setDocument(documentResponse);
      setFacts(factsResponse);
      setLoanTape(tapeResponse);
      setLoan(loanResponse);
      setPackageEvidence(pkgEvidence);
      setDocumentTypes(
        Array.isArray(typesResponse?.items)
          ? typesResponse.items.map((row) => ({
              id: row.id,
              label: row.label || row.id,
            }))
          : []
      );
      setSelectedDocumentId(
        documentResponse?.source_file_id
          ? String(documentResponse.source_file_id)
          : pkgEvidence?.items?.[0]
            ? String(pkgEvidence.items[0].file_id)
            : null
      );
      if (FEATURES.labelledCopies && packageId) {
        try {
          const contexts = await listPackageCopyContexts(packageId);
          setCopyContexts(Array.isArray(contexts) ? contexts : []);
        } catch {
          setCopyContexts([]);
        }
      } else {
        setCopyContexts([]);
      }
      return factsResponse;
    } catch (requestError) {
      setDocument(null);
      setFacts(null);
      setLoan(null);
      setDocumentTypes([]);
      setCopyContexts([]);
      setError(formatLoadFailure(requestError, 'Unable to load document review.'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [assetId, packageId, valid, viewAllowed]);

  const pollJobRef = useRef(null);
  pollJobRef.current = pollJob;

  useEffect(() => {
    let cancelled = false;
    load().then((factsResponse) => {
      if (cancelled || !factsResponse || pollJobRef.current) return;
      // A background validate/extract started earlier (or in another tab)
      // must stay visible after a reload: resume polling or show the failure.
      const onLoad = syncJobOnLoad(factsResponse);
      if (onLoad.resume && mutateAllowed) {
        setPollJob(onLoad.resume);
        setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
        setVaBusy(true);
        setVaMessage(onLoad.message);
      } else if (onLoad.message) {
        setVaMessage((current) => current || onLoad.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [load, reloadToken, mutateAllowed]);

  useEffect(() => {
    if (!valid || !viewAllowed || !mutateAllowed || !pollUntil) return undefined;
    if (Date.now() >= pollUntil) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() >= pollUntil) {
        setPollUntil(0);
        setPollJob(null);
        setVaBusy(false);
        setVaMessage((msg) =>
          msg ||
          'Still waiting on ABDP types — click Run classification again to re-sync.'
        );
        return;
      }
      try {
        // Facts only — never POST classify here. Re-running classify while
        // types are empty re-kicks upload_contract_batch_base on ABDP.
        const factsResponse = await getOriginatorExtractedFacts(assetId);
        if (cancelled) return;
        if (pollJob) {
          // Background job (large batch): wait for ABDP_SYNC_JOB to finish.
          const outcome = syncJobPollOutcome(factsResponse, { startedAt: pollJob.startedAt });
          setVaMessage(
            outcome.message ||
              `${pollJob.label} is running in the background — this page refreshes when it finishes.`
          );
          if (outcome.done) {
            setPollUntil(0);
            setPollJob(null);
            setVaBusy(false);
            setFacts(factsResponse);
            if (!outcome.failed) {
              setReloadToken((value) => value + 1);
              if (pollJob.action === 'extract') {
                const params = new URLSearchParams(searchParams);
                params.set('step', 'review');
                setSearchParams(params, { replace: true });
              }
            }
          }
          return;
        }
        // Post-kick follow-up: the backend re-syncs from Process every minute
        // and reports progress in ABDP_SYNC_JOB.
        const followUp = syncJobPollOutcome(factsResponse, {
          startedAt: classifyStartedRef.current,
        });
        if (followUp.failed) {
          setPollUntil(0);
          setVaBusy(false);
          setFacts(factsResponse);
          setVaMessage(followUp.message);
          return;
        }
        setVaMessage(followUp.message || 'Checking ABDP for document types…');
        if (cancelled) return;
        setFacts(factsResponse);
        const ready = (factsResponse?.items || []).some(
          (f) =>
            typeof f.fact_key === 'string' &&
            f.fact_key.startsWith('DOCUMENT_TYPE_') &&
            f.fact_value &&
            f.fact_value !== 'CLASSIFY_PENDING_OR_EMPTY'
        );
        if (ready) {
          setPollUntil(0);
          setVaBusy(false);
          setVaMessage('Classification types synced.');
          setReloadToken((value) => value + 1);
        }
      } catch {
        // ignore transient poll errors
      }
    };
    const id = window.setInterval(tick, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [assetId, valid, viewAllowed, mutateAllowed, pollUntil, pollJob, searchParams, setSearchParams]);

  const documents = useMemo(() => {
    const byId = new Map();
    if (document?.source_file_id) {
      byId.set(String(document.source_file_id), {
        id: String(document.source_file_id),
        label: document.source_file_name || 'Source file',
        fileName: document.source_file_name || undefined,
      });
    }
    (packageEvidence?.items || []).forEach((file, index) => {
      const id = String(file.file_id);
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          label: file.file_name || `Page ${index + 1}`,
          fileName: file.file_name || undefined,
        });
      }
    });
    (facts?.items || []).forEach((fact) => {
      if (!fact.source_file_id) return;
      const id = String(fact.source_file_id);
      if (!byId.has(id)) {
        byId.set(id, { id, label: `File ${id.slice(0, 8)}`, fileName: undefined });
      }
    });
    return Array.from(byId.values()).map((doc) => {
      const context = copyContexts.find(
        (item) => String(item.package_file_id) === String(doc.id)
      );
      return { ...doc, copyKind: context?.copy_kind };
    });
  }, [copyContexts, document, facts, packageEvidence]);

  const activeContext = useMemo(
    () =>
      copyContexts.find(
        (item) => String(item.package_file_id) === String(selectedDocumentId)
      ) ||
      copyContexts[0] ||
      null,
    [copyContexts, selectedDocumentId]
  );

  const factRows = useMemo(
    () =>
      (facts?.items || []).map((fact) => ({
        id: fact.id,
        key: fact.fact_key,
        value: fact.fact_value,
        verificationStatus: fact.verification_status || 'unverified',
        verifiedAt: fact.verified_at || null,
        verifiedByLabel: fact.verified_by_label || null,
      })),
    [facts]
  );

  const loadDocumentPreview = useCallback(
    async (doc, { member } = {}) => {
      if (!doc?.id) {
        throw new Error('Document file is required to load the source document.');
      }
      if (FEATURES.labelledCopies && packageId) {
        return downloadPackageFileRendition(packageId, doc.id, { disposition: 'inline', member });
      }
      if (member && packageId) {
        return downloadPackageFileRendition(packageId, doc.id, { disposition: 'inline', member });
      }
      if (assetId && document?.source_file_id) {
        try {
          return await downloadOriginatorDocumentFileContent(assetId, doc.id);
        } catch {
          // Fall through to package file download when asset-scoped path fails.
        }
      }
      if (!packageId) {
        throw new Error('Package file is required to load the source document.');
      }
      return downloadPackageFileContent(packageId, doc.id);
    },
    [assetId, document?.source_file_id, packageId]
  );

  // ZIP batches: list PDF members server-side so one member is fetched at a time.
  const listDocumentMembers = useCallback(
    async (doc) => {
      if (!packageId || !doc?.id) {
        throw new Error('Package file is required to list ZIP members.');
      }
      return listPackageFileMembers(packageId, doc.id);
    },
    [packageId]
  );

  async function handleDownload(doc) {
    if (!packageId || !doc?.id) return;
    const result = await downloadPackageFileRendition(packageId, doc.id, {
      disposition: 'attachment',
    });
    if (!result?.blob) return;
    saveBlob(
      result.blob,
      downloadFileName({
        fileName: result.fileName,
        label: doc.fileName,
        fallback: 'non-authoritative-copy',
        mimeType: result.mimeType || result.blob.type,
      })
    );
  }

  const extractEnabled = useMemo(() => {
    const items = facts?.items || [];
    const ready = items.find((f) => f.fact_key === 'ABDP_READY_FOR_EXTRACT');
    if (ready && String(ready.fact_value).toLowerCase() === 'true') return true;
    return items.some(
      (f) =>
        typeof f.fact_key === 'string' &&
        f.fact_key.startsWith('DOCUMENT_TYPE_') &&
        f.fact_value &&
        f.fact_value !== 'CLASSIFY_PENDING_OR_EMPTY'
    );
  }, [facts]);

  const runVa = useCallback(
    async (action) => {
      const syncActions = new Set(['extract', 'validate', 'sync_review', 'tape_validate']);
      const allowed =
        action === 'classify' ? mutateAllowed : syncActions.has(action) ? canSyncFromAbdp : mutateAllowed;
      if (!assetId || !allowed) return false;
      setVaBusy(true);
      setVaMessage(
        action === 'classify'
          ? 'Classify is queued. Process can take hours for large batches — this page will refresh when types appear. Use Sync when Review is ready.'
          : action === 'extract'
            ? 'Running extraction… fields will appear on Review when ready.'
            : action === 'sync_review'
              ? 'Syncing Process review from ABDP… Digitize will mark fields verified when the folder is in review.'
              : ''
      );
      if (action === 'classify') {
        classifyStartedRef.current = Date.now();
        setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
      }
      let queued = false;
      try {
        const result =
          action === 'validate'
            ? await runValidateToCompletion(() => runOriginatorVaAction(assetId, 'validate'), {
                onProgress: (page) => setVaMessage(validateProgressMessage(page)),
              })
            : await runOriginatorVaAction(assetId, action);
        setVaMessage(
          result?.message ||
            `${action} completed` + (result?.eid ? ` (eid=${result.eid})` : '')
        );
        if (isSyncJobQueuedResponse(result)) {
          // Large batch: the backend scheduled the sync; poll ABDP_SYNC_JOB.
          queued = true;
          setPollJob({
            action,
            startedAt: Date.now(),
            label: syncJobActionLabel(action),
          });
          setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
          setVaBusy(true);
          return true;
        }
        setReloadToken((value) => value + 1);
        const pending =
          action === 'classify' &&
          typeof result?.message === 'string' &&
          /not ready|empty|in a minute|re-sync|could not read|auto-pipeline (running|kicked)|queued/i.test(
            result.message
          );
        if (!pending) {
          setPollUntil(0);
          setVaBusy(false);
        }
        if (action === 'extract') {
          const params = new URLSearchParams(searchParams);
          params.set('step', 'review');
          setSearchParams(params, { replace: true });
        }
        return true;
      } catch (requestError) {
        setVaMessage(vaActionErrorMessage(action, requestError));
        if (action !== 'classify') {
          setVaBusy(false);
          setPollUntil(0);
        }
        return false;
      } finally {
        if (action !== 'classify' && !queued) {
          setVaBusy(false);
        }
      }
    },
    [assetId, canSyncFromAbdp, mutateAllowed, searchParams, setSearchParams]
  );

  const applyFactsResponse = useCallback((factsResponse) => {
    if (factsResponse) setFacts(factsResponse);
  }, []);

  const handleVerifyFact = useCallback(
    async (factId) => {
      if (!assetId || !mutateAllowed || !factId) return;
      setVerifyBusy(true);
      try {
        const updated = await verifyOriginatorFact(assetId, factId);
        applyFactsResponse(updated);
      } catch (requestError) {
        setVaMessage(formatCustodyFailure(requestError, 'Unable to verify field.'));
      } finally {
        setVerifyBusy(false);
      }
    },
    [assetId, mutateAllowed, applyFactsResponse]
  );

  const handleVerifyDocumentType = useCallback(
    async (documentTypeId) => {
      if (!assetId || !mutateAllowed || !documentTypeId) return;
      setVerifyBusy(true);
      try {
        const updated = await verifyOriginatorFactsBatch(assetId, {
          document_type_id: documentTypeId,
        });
        applyFactsResponse(updated);
      } catch (requestError) {
        setVaMessage(formatCustodyFailure(requestError, 'Unable to verify fields.'));
      } finally {
        setVerifyBusy(false);
      }
    },
    [assetId, mutateAllowed, applyFactsResponse]
  );

  const refreshDocumentTypes = useCallback(async () => {
    if (!assetId) return;
    try {
      const typesResponse = await getOriginatorDocumentTypes(assetId);
      setDocumentTypes(
        Array.isArray(typesResponse?.items)
          ? typesResponse.items.map((row) => ({
              id: row.id,
              label: row.label || row.id,
            }))
          : []
      );
    } catch {
      // Keep prior catalog on failure.
    }
  }, [assetId]);

  const handleSaveClassification = useCallback(
    async ({ jobId, documentTypeId, documentTypeLabel }) => {
      if (!assetId || !mutateAllowed || !jobId || !documentTypeId) return;
      setEditBusy(true);
      setVaMessage('');
      try {
        const updated = await patchOriginatorClassification(assetId, {
          job_id: jobId,
          document_type_id: documentTypeId,
          document_type_label: documentTypeLabel,
        });
        applyFactsResponse(updated);
        setVaMessage('Document type saved to ABDP.');
        await refreshDocumentTypes();
      } catch (requestError) {
        setVaMessage(formatCustodyFailure(requestError, 'Unable to save document type.'));
      } finally {
        setEditBusy(false);
      }
    },
    [assetId, mutateAllowed, applyFactsResponse, refreshDocumentTypes]
  );

  const handleSaveExtractedFields = useCallback(
    async (updates) => {
      if (!assetId || !mutateAllowed || !updates?.length) return;
      setEditBusy(true);
      setVaMessage('');
      try {
        const updated = await patchOriginatorExtractedFields(assetId, { updates });
        applyFactsResponse(updated);
        setVaMessage('Field corrections saved to ABDP.');
      } catch (requestError) {
        setVaMessage(formatCustodyFailure(requestError, 'Unable to save field corrections.'));
      } finally {
        setEditBusy(false);
      }
    },
    [assetId, mutateAllowed, applyFactsResponse]
  );

  const handleMarkReviewed = useCallback(async () => {
    if (!assetId || !mutateAllowed) return;
    setEditBusy(true);
    setVaMessage('');
    try {
      const updated = await markOriginatorReviewed(assetId);
      applyFactsResponse(updated);
      setVaMessage('ABDP review confirmed. Loading validation rules…');
      await runVa('validate');
    } catch (requestError) {
      setVaMessage(formatCustodyFailure(requestError, 'Unable to mark reviewed in ABDP.'));
    } finally {
      setEditBusy(false);
    }
  }, [assetId, mutateAllowed, applyFactsResponse, runVa]);

  const title =
    document?.loan_number ||
    document?.asset_number ||
    loan?.loan_id ||
    loan?.asset_number ||
    'Digitize';
  const subtitle = document
    ? `${document.batch || '—'} · ${document.program || '—'} · ${document.borrower || '—'}`
    : loan
      ? `${loan.batch || '—'} · ${loan.program || '—'} · ${loan.borrower || '—'}`
      : undefined;

  return (
    <div className="space-y-4">
      {inCustody ? (
        <InCustodyNotice>
          {roleMutateAllowed && FEATURES.labelledCopies ? (
            <button
              type="button"
              className="text-[13px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
              onClick={() => setRequestOpen(true)}
            >
              Raise a custody request →
            </button>
          ) : null}
        </InCustodyNotice>
      ) : certifiedLock ? (
        <CertifiedLockNotice />
      ) : null}
      <DigitizeWorkspace
        title={title}
        subtitle={subtitle}
        loading={loading}
        error={error}
        onRetry={valid ? () => setReloadToken((value) => value + 1) : undefined}
        documents={documents}
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={setSelectedDocumentId}
        loadDocumentPreview={loadDocumentPreview}
        listDocumentMembers={listDocumentMembers}
        facts={factRows}
        validationAssetId={assetId}
        fetchValidationPage={getOriginatorEvidencePage}
        fetchValidationDetails={getOriginatorEvidenceDetails}
        validationReloadToken={reloadToken}
        canMutateVa={mutateAllowed}
        canSyncFromAbdp={canSyncFromAbdp}
        onRunClassify={mutateAllowed ? () => runVa('classify') : undefined}
        onRunExtract={canSyncFromAbdp ? () => runVa('extract') : undefined}
        onRunValidate={canSyncFromAbdp ? () => runVa('validate') : undefined}
        loanTape={loanTape}
        onRunTapeValidate={canSyncFromAbdp ? () => runVa('tape_validate') : undefined}
        onSyncReview={canSyncFromAbdp ? () => runVa('sync_review') : undefined}
        queueHint={[document?.batch, document?.program, loan?.batch, loan?.program]
          .filter(Boolean)
          .join(' ')}
        vaBusy={vaBusy}
        vaMessage={vaMessage}
        extractEnabled={extractEnabled}
        extractionState={facts?.extraction_state || 'none'}
        fieldTotal={facts?.field_total || 0}
        fieldVerified={facts?.field_verified || 0}
        onVerifyFact={mutateAllowed ? handleVerifyFact : undefined}
        onVerifyDocumentType={mutateAllowed ? handleVerifyDocumentType : undefined}
        verifyBusy={verifyBusy}
        documentTypes={documentTypes}
        onSaveClassification={mutateAllowed ? handleSaveClassification : undefined}
        onSaveExtractedFields={mutateAllowed ? handleSaveExtractedFields : undefined}
        onMarkReviewed={mutateAllowed ? handleMarkReviewed : undefined}
        editBusy={editBusy}
        labelledCopies={FEATURES.labelledCopies}
        copyContext={activeContext}
        onDownloadCopy={FEATURES.labelledCopies ? handleDownload : undefined}
        onRequestCertifiedCopy={
          FEATURES.labelledCopies && roleMutateAllowed
            ? () => setRequestOpen(true)
            : undefined
        }
      />
      <CreateRequestModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        assetId={assetId}
        lockAsset
        defaultType="COPY_REQUEST"
        custodyState={document?.custody_state ?? loan?.custody_state ?? null}
      />
      <Link
        to={packageId ? `/packages/${packageId}?view=assets` : '/packages'}
        className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← Back to package loans
      </Link>
    </div>
  );
}
